import { detectBrowser, detectBrowserVersion, detectOS } from "./auditLogger";

/**
 * Posts RShield focus events to the API so they land in the rShieldFocusLog
 * collection.
 *
 * Endpoint: POST {REACT_APP_API}/docs/rshieldfocuslog
 *
 * The reader email is deliberately NOT sent - the API resolves it from the
 * open record so it cannot be spoofed. Everything here is best effort: a
 * failed post must never interrupt or block the reader, so errors are
 * swallowed and nothing is awaited.
 *
 * @param {object} ctx
 * @param {string} ctx.documentId
 * @param {string} ctx.openId     open record id the reader is viewing under
 * @param {string} ctx.sessionId
 * @param {() => number} [ctx.getPageNumber]  page the reader is on, read at
 *        send time so page navigation does not need to rebuild the reporter

 */
export function createRShieldFocusReporter(ctx = {}) {
  const {
    documentId = "",
    openId = "",
    sessionId = "",
    getPageNumber,
    onRedirect,
  } = ctx;

  const browser = detectBrowser();
  const browserVersion = detectBrowserVersion();
  const operatingSystem = detectOS();

  const url = `${process.env.REACT_APP_API}/docs/rshieldfocuslog`;

  /**
   * @param {string} eventType  RShieldFocusLoss | RShieldAccessRevoked |
   *                            RShieldCaptureDetected
   * @param {object} [details]  { Reason, FocusLossCount, FocusLossLimit }
   */
  function report(eventType, details = {}) {
    try {
      if (!documentId || !openId) return;

      let pageNumber = 0;
      try {
        if (typeof getPageNumber === "function") {
          pageNumber = Number(getPageNumber()) || 0;
        }
      } catch (e) {
        pageNumber = 0;
      }

      const payload = {
        DocumentId: documentId,
        OpenId: openId,
        EventType: eventType,
        // Client clock reading for the blur. The API also stamps its own
        // receive time, which is what ordering should rely on.
        FocusBlurTime: new Date().toISOString(),
        Reason: details.Reason || null,
        FocusLossCount: Number(details.FocusLossCount || 0),
        FocusLossLimit: Number(details.FocusLossLimit || 0),
        // Shown as Page Number(s) on the capture detection notice.
        PageNumber: pageNumber,
        Browser: browser,
        BrowserVersion: browserVersion,
        OperatingSystem: operatingSystem,
        SessionId: sessionId,
      };

      if (typeof fetch !== "function") return;

      // keepalive lets the request survive the reader navigating away or the
      // tab being closed straight after a focus loss.
      fetch(url, {
        method: "POST",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      })
        .then((response) => (response && response.ok ? response.json() : null))
        .then((data) => {
          if (data && data.authenticated && data.redirectUrl) {
            if (typeof onRedirect === "function") {
              onRedirect(data.redirectUrl);
              return;
            }
            window.open(data.redirectUrl, "_self");
          }
        })
        .catch(() => {});
    } catch (e) {
      /* never throw from the reporter */
    }
  }

  const fullScreenKey = `rshield_fsread_${documentId}_${openId}`;

  function reportFirstFullScreen() {
    try {
      if (!documentId || !openId) return;

      try {
        if (sessionStorage.getItem(fullScreenKey) === "true") return;
        sessionStorage.setItem(fullScreenKey, "true");
      } catch (e) {
        /* sessionStorage unavailable - the server side guard still holds */
      }

      if (typeof fetch !== "function") return;

      fetch(`${process.env.REACT_APP_API}/docs/rshieldfullscreenread`, {
        method: "POST",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          DocumentId: documentId,
          OpenId: openId,
          SessionId: sessionId,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch (e) {
      /* never throw from the reporter */
    }
  }

  return { report, reportFirstFullScreen };
}

export default createRShieldFocusReporter;
